import graphqlClient from '@/graphql/graphql-client';
import { organizationIdForQuery, toListQueryVariables } from '@/graphql/list-query';
import type { ListQueryVariables } from '@/graphql/types';
import { useQuery } from '@tanstack/react-query';
import {
  OrgArchivedContractDocumentsDocument,
  OrgArchivedContractDocumentsFilteredDocument,
  OrgContractDocumentByIdDocument,
  OrgContractDocumentsByStatusAndTitleDocument,
  OrgContractDocumentsByStatusDocument,
  OrgContractDocumentsByTitleDocument,
  OrgContractDocumentsDocument,
  OrgTrashedContractDocumentsDocument,
  OrgTrashedContractDocumentsFilteredDocument,
  type OrgContractDocumentsQuery,
} from '@/graphql/generated/graphql';

export type ContractDocumentItem = NonNullable<
  NonNullable<OrgContractDocumentsQuery['getOrgContractDocuments']>['items']
>[number];
export type GetOrgContractDocumentsResponse = OrgContractDocumentsQuery;

type ContractWhere = {
  ItemId?: { eq?: string | null }
  OrganizationId?: { eq?: string | null }
  IsArchived?: { eq?: boolean | null }
  IsInTrash?: { eq?: boolean | null }
  Status?: { in?: string[] | null }
  TemplateTitle?: { contains?: string | null }
}

export const toOrgContractDocumentsRequest = (
  params?: ListQueryVariables<Record<string, unknown>> | null
) => {
  const { where, paging } = toListQueryVariables(params);
  const w = (where ?? {}) as ContractWhere;
  const pageNo = paging?.pageNo ?? 1;
  const pageSize = paging?.pageSize ?? 10;
  const organizationId = w.OrganizationId?.eq ?? organizationIdForQuery(where);
  const templateTitleContains = w.TemplateTitle?.contains?.trim() || null;
  const statusIn = w.Status?.in?.length ? w.Status.in : null;

  if (w.ItemId?.eq) {
    return {
      document: OrgContractDocumentByIdDocument,
      variables: { itemId: w.ItemId.eq, pageNo, pageSize },
    };
  }

  if (w.IsInTrash?.eq === true) {
    return templateTitleContains
      ? {
        document: OrgTrashedContractDocumentsFilteredDocument,
        variables: { organizationId, templateTitleContains, pageNo, pageSize },
      }
      : {
        document: OrgTrashedContractDocumentsDocument,
        variables: { organizationId, pageNo, pageSize },
      };
  }

  if (w.IsArchived?.eq === true) {
    return templateTitleContains
      ? {
        document: OrgArchivedContractDocumentsFilteredDocument,
        variables: { organizationId, templateTitleContains, pageNo, pageSize },
      }
      : {
        document: OrgArchivedContractDocumentsDocument,
        variables: { organizationId, pageNo, pageSize },
      };
  }

  if (statusIn && templateTitleContains) {
    return {
      document: OrgContractDocumentsByStatusAndTitleDocument,
      variables: { organizationId, statusIn, templateTitleContains, pageNo, pageSize },
    };
  }

  if (statusIn) {
    return {
      document: OrgContractDocumentsByStatusDocument,
      variables: { organizationId, statusIn, pageNo, pageSize },
    };
  }

  if (templateTitleContains) {
    return {
      document: OrgContractDocumentsByTitleDocument,
      variables: { organizationId, templateTitleContains, pageNo, pageSize },
    };
  }

  return {
    document: OrgContractDocumentsDocument,
    variables: { organizationId, pageNo, pageSize },
  };
};

export const orgContractDocumentsQueryKey = (variables: Record<string, unknown>) =>
  ['org-contract-documents', variables] as const;

export const orgContractDocumentByIdQueryKey = (itemId: string) =>
  orgContractDocumentsQueryKey({ itemId, pageNo: 1, pageSize: 1 });

export const useGetOrgContractDocuments = (
  params?: ListQueryVariables<Record<string, unknown>>,
  options?: { enabled?: boolean }
) => {
  const request = toOrgContractDocumentsRequest(params);
  const w = (params?.where ?? {}) as ContractWhere;
  const enabled = options?.enabled ?? Boolean(w.ItemId?.eq || organizationIdForQuery(params?.where));

  return useQuery({
    queryKey: orgContractDocumentsQueryKey(request.variables),
    queryFn: () =>
      graphqlClient.request(request.document, request.variables),
    enabled,
  });
};

export const useGetOrgContractDocumentById = (itemId: string, enabled = true) => {
  const query = useGetOrgContractDocuments(
    { where: { ItemId: { eq: itemId } }, paging: { pageNo: 1, pageSize: 1 } },
    { enabled: Boolean(itemId) && enabled }
  );

  return {
    ...query,
    data: query.data?.getOrgContractDocuments?.items?.[0] ?? null,
  };
};

export const requestOrgContractDocuments = (
  params?: ListQueryVariables<Record<string, unknown>> | null
) => {
  const request = toOrgContractDocumentsRequest(params);
  return graphqlClient.request(request.document, request.variables);
};

export const GET_ORG_CONTRACT_DOCUMENTS_QUERY = OrgContractDocumentsDocument;
